import { Typography, Box, Stack } from "@mui/material";
import EndIcon from "src/assets/svg-icons/end.svg";
import { UseFormWatch } from "react-hook-form";
import { FormData } from "../../constants";
import { COGSTypes } from "src/types/generic";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils";

const Summary = ({
  journeyData,
  watch,
  COGSType,
  journeyTaxPercentage,
}: {
  journeyData: any;
  watch: UseFormWatch<FormData>;
  COGSType: COGSTypes;
  journeyTaxPercentage: number;
}) => {
  const journeyTaxDivider = 1 + journeyTaxPercentage;
  const { t } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");

  const netSales =
    watch("journey_type") === "foodics"
      ? watch("has_jazr_calculations") === "no"
        ? journeyData?.sale?.foodics_net_sales
        : journeyData?.sale.gross_sales / journeyTaxDivider
      : watch("journey_type") === "excel"
      ? journeyData?.sale.gross_sales / journeyTaxDivider
      : +watch("net_sales_with_tax") / journeyTaxDivider;

  const netSalesWithTax =
    watch("journey_type") === "manual"
      ? +watch("net_sales_with_tax")
      : journeyData?.sale.gross_sales;

  const orderQuantity =
    watch("journey_type") === "manual"
      ? watch("total_order_quantity")
      : journeyData?.sale?.quantity;

  const jazrTax =
    (journeyData?.sale.gross_sales / journeyTaxDivider) * journeyTaxPercentage;

  const allTax =
    watch("journey_type") === "foodics"
      ? watch("has_jazr_calculations") === "no"
        ? journeyData.sale.foodics_net_sales_tax
        : jazrTax
      : watch("journey_type") === "excel"
      ? jazrTax
      : (+watch("net_sales_with_tax") / journeyTaxDivider) *
        journeyTaxPercentage;

  const cogs =
    COGSType === "manual"
      ? (watch("COGS_manual_type") === "amount"
          ? +watch("COGS_value")
          : (watch("COGS_value") / 100) * netSales
        ).toFixed(2) ?? "0"
      : journeyData?.cogs?.recipe_amount;

  const cogsPercentage =
    COGSType === "manual"
      ? (watch("COGS_manual_type") === "percentage"
          ? watch("COGS_value")
          : ((watch("COGS_value") / netSales) * 100).toFixed(2)) ?? "--"
      : ((journeyData?.cogs?.recipe_amount / netSales) * 100).toFixed(2);

  return (
    <div>
      <Box display={"flex"} justifyContent={"center"}>
        <Stack maxWidth={"736px"} width={"100%"} px={{ xs: 3, md: 0 }}>
          <Typography fontSize={"26px"} fontWeight={600}>
            {generalT("summary")}
          </Typography>
          <Typography color={"textSecondary"}>{t("hereWeGetEnd")}</Typography>
          <Box my={3} display={"flex"} justifyContent={"center"}>
            <img src={EndIcon} alt="" />
          </Box>
          <Stack gap={1.5} alignItems={"flex-start"}>
            <Box width={"100%"}>
              <Box height={"7px"} bgcolor={"#D0D5DD"} />
              <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {t("timePeriod")}
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {watch("journey_type") === "manual"
                      ? watch("dateRange.start_date")
                      : journeyData?.start_date}{" "}
                    {generalT("to")}{" "}
                    {watch("journey_type") === "manual"
                      ? watch("dateRange.end_date")
                      : journeyData?.end_date}
                  </Typography>
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("netSalesWithTax")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {formatNumber(netSalesWithTax) ?? "--"} {generalT("sar")}
                  </Typography>
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("netSales")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {formatNumber(netSales.toFixed(2)) ?? "--"}{" "}
                    {generalT("sar")}
                  </Typography>
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("tax")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {formatNumber(allTax.toFixed(2)) ?? "--"} {generalT("sar")}
                  </Typography>
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("orderQuantity")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {formatNumber(orderQuantity) ?? "--"}
                  </Typography>
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("cogs")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {formatNumber(cogs)} {generalT("sar")}
                  </Typography>
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("cogs")} {t("percentage")}:
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {cogsPercentage} %
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </div>
  );
};

export default Summary;
